/**
 * 이상한 방 - 체인저의 손 (사람 체인저의 바꾸기 도구)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(바꾸기 1분) · 4.2절(클릭 → 동사 팔레트 → 즉시 실행) ·
 *            4.3절(거부는 오류 메시지가 아니라 선택 불가) · 7절("12개/60초가 사람에게 되는가" - 이 파일이 그 실험의 손이다)
 *            `이상한방-상태모델-v0.1.md` 5.1절(한 물건 한 번) · 11.3절(변화 검증은 서버가 쥔다)
 *            로드맵 M4(`changerTool` - `editor.ts`를 키우지 않는다 · 낙관적 초안은 화면만 · 거부가 오면 되돌린다)
 *
 * ## 탭 수가 이 파일의 성적표다
 *
 * 없어짐·돌아감·색 바뀜은 **물건 탭 + 동사 탭 = 2탭**, 옮김은 3탭(자리 탭이 더), 생김은 소품이 이미 골라져 있으면
 * 2탭이다(동사 탭 + 자리 탭 - 그래서 생김은 자리를 탭한 뒤에도 그 소품을 든 채로 남는다. 같은 것을 셋 놓는 데 여섯 탭이면
 * 60초가 모자란다). 돌아감은 "보이는 다음 회전"을, 색 바뀜은 "보이는 다음 색"을 도구가 고른다 - 팔레트에서 특정 색을
 * 직접 탭하는 길도 있지만(선택 중 색 팔레트) 기본 동작은 한 탭이다.
 *
 * ## 규칙을 판단하지 않는다 - 미리 검사해 잠글 뿐이다
 *
 * 팔레트 버튼을 잠그는 것은 `core/verbs`의 `apply*`를 **지금 초안에 실제로 돌려 본 결과**다(4.3절, `changerLocks.ts`).
 * 예산이 0이면 전부 잠기는데, 그 수는 호스트가 뷰에 내린 `desk.budgetLeft`(리듀서 `budgetLeftOf` 그대로)에서 아직 답이 안 온
 * 요청 수를 뺀 **표시용 숫자**다(회색 고리와 같은 낙관). `budget - used`를 여기서 다시 세지 않는다. 최종 판정은 언제나
 * 호스트(`matchChange.ts`)이고, 여기서 잠그지 못한 것(자리 탭 · 지연)은 거부로 돌아온다.
 *
 * 옮김의 자리 탭 하나만 도구가 **요청을 규칙에 맞춰 만든다** - 표면 스냅이 준 자리의 y를 물건의 y로 바꿔 보낸다. 옮김은 높이(y)를
 * 바꾸지 않는다는 규칙(`core/verbs.shouldKeepHeightOnMove`, 생성기 `tryMove`와 같다)을 판단하는 것이 아니라, 그 규칙에 걸리지 않는
 * 요청을 만드는 것이다. 바닥을 눌러 벽의 거울을 바닥에 눕히려던 탭이 거부 한 번이 아니라 변화 하나(벽 높이의 옮김)가 된다.
 *
 * ## 고친 값을 다시 고칠 수 있다 - 5.1절은 겹치기를 막는 규칙이다
 *
 * 5.1절이 막는 것은 **한 물건에 변화가 둘 쌓이는 것**이다("옮기고 색까지 바꾸면 예산은 2인데 술래의 인지는 1개").
 * 값을 다시 고르는 것은 그 물건의 변화를 **갈아 끼우는** 것이라, 끝나고 나면 여전히 변화 하나 · 예산 하나 · 판정 하나다.
 * 그래서 이미 건드린 물건도 다시 고를 수 있다 - 90°를 두 번 눌러 180°로 두고, 잘못 놓은 것을 다시 옮긴다.
 *
 * 호스트에는 새 규칙이 없다. 갈아 끼우기는 `undoChange` 하나 + `applyChange` 하나를 **한 손으로** 보내는 것이고
 * (`reedit`), 예산은 -1 +1로 0, 변화 id는 `desk-${targetId}`라 갈아 끼운 뒤에도 같다 - `matchChange.ts` 머리말이
 * "되돌린 뒤 다시 건드려도 같은 id가 되어 되돌리기 카운터가 필요 없다"고 이미 적어 둔 그 자리다.
 *
 * **다음 값은 되돌린 원본이 아니라 지금 보이는 것에서 고른다.** 되돌린 방에서 고르면 회전을 몇 번 눌러도 늘 90°가
 * 나온다 - 되돌리기는 보내는 쪽에서만 일어나고, 잠금과 다음 값은 지금 초안에서 잰다(`editCtx`).
 *
 * 내가 놓은 물건은 갈래가 하나 더다. 그 물건의 변화는 `add` 하나뿐이라 회전도 색도 자리도 **그 `add`의 필드**를
 * 고치는 것이다(`fieldRequest`) - `changerLocks.ADD_ROT`가 "돌리는 것은 놓기 전에 정해진 값"이라 적어 둔 그 값을
 * 여기서 정한다. id는 그대로 쓴다(되돌리기가 초안에서 빼 두므로 중복이 아니고, 그래야 변화 id도 선택도 안 움직인다).
 *
 * ## 낙관적 초안 - 화면만 앞서가고 거부가 오면 되돌린다
 *
 * 요청을 보내는 순간 같은 `core/verbs`로 초안을 그려 둔다(`confirmed` + `pending`). 호스트의 `changerProgress`가
 * 수용의 신호다(수용된 요청마다 정확히 하나 온다 - 되돌리기도 같다). `changeRejected`나 `rejected{cseq}`가 오면 그 요청을
 * 빼고 **확정 초안 위에 남은 요청을 다시 돌려** 화면을 되돌린다. 호스트가 같은 초안을 다시 보내지 않으므로(`matchChange.ts`
 * 머리말 "수용마다 `rooms`를 보내지 않는다") 확정 초안은 여기가 든다.
 *
 * 다시 돌리다 이제 안 되는 요청(첫 요청이 거부되어 둘째의 자리가 도로 막혔다)은 **버리지 않고 `dropped`로 남긴다.** 호스트도
 * 그 요청에 같은 거부를 보낼 것이고, 그 답은 순서대로 온다 - 자리를 비워 두면 다음 거부가 그 뒤의 멀쩡한 요청을 대신 떨어뜨려
 * 초안이 호스트와 어긋난다(루프백은 마이크로태스크 배달이라 드물지만 소켓에서는 지연마다 생긴다). `dropped`는 초안에도 개수에도
 * 들지 않고, 자기 답(거부)이 와서 빠지기만을 기다린다.
 *
 * ## Three.js를 모른다
 *
 * 탭 → 대상은 `pick`으로, 선택 외곽선은 `onSelectionChanged`로 바깥(`app/stageChanger.ts`)이 붙인다. 그래서 이 상태 머신은
 * node에서 그대로 검사된다(`changerTool.test.ts`). `input/`은 `core`·`catalog`만 본다 - 뷰·이벤트의 모양은 여기서 좁게
 * 다시 적고(`ChangerDeskView`·`ChangerToolEvent`) 접착층(`game/matchSessionChanger.ts`)이 구조적으로 맞춘다.
 */

import type { CatalogEntry, CatalogId, Cell3, InstanceId, ObjectInstance, PaletteIndex, RoomState, Rot, Verb } from '../core/types'
import { applyVerbRequest, changerVerbContext, isInAllowedRect, restoreOriginal, type VerbContext, type VerbRequest, type VerbResult } from '../core/verbs'
import { ADD_ROT, addLock, lockFor, nextVisibleColor, nextVisibleRot, pickChangerCatalog } from './changerLocks'
import type { PickResult } from './tapPicker'
import type { ChangerBarView, ChangerModeState, ChangerTool, ChangerToolDeps, ChangerToolWager } from './changerToolTypes'
import { buildChangerBarView } from './changerToolView'

/* --- 모양 -------------------------------------------------------------------------- */

/*
 * 바·접착층·무대가 읽는 모양은 `changerToolTypes.ts`에 있다. 여기서 다시 내보내는 것은 바깥이 "도구"라는 이름 하나로
 * 찾게 하려는 것이다 - `ui/changerBar.ts`·`game/matchSessionChanger.ts`·`app/stageChanger.ts`가 전부 이 파일을 import 한다.
 */
export {
  CHANGER_VERBS, type ChangerBarView, type ChangerCatalogView, type ChangerColorView, type ChangerDeskView, type ChangerLockReason,
  type ChangerPick, type ChangerPreview, type ChangerTool, type ChangerToolDeps, type ChangerToolEvent, type ChangerToolMode, type ChangerToolWager, type ChangerVerbView, type ChangerViewSnapshot,
} from './changerToolTypes'

/* --- 내부 타입 -------------------------------------------------------------------- */

/** 모드의 모양은 `changerToolTypes.ChangerModeState`다 - 뷰 조립(`changerToolView`)이 같은 것을 읽는다. */
type Mode = ChangerModeState

/**
 * 답을 기다리는 요청. `after`는 그 요청을 적용한 뒤의 초안이다 - 수용되면 그것이 확정 초안이 된다.
 * `dropped`는 다시 돌리다 실패해 초안에서 빠진 요청의 **자리**다(머리말) - 호스트의 거부가 와서 이 자리를 소비한다.
 */
type Pending =
  | Readonly<{ kind: 'apply'; cseq: number; request: VerbRequest; targetId: InstanceId; after: RoomState }>
  | Readonly<{ kind: 'undo'; cseq: number; id: InstanceId; after: RoomState }>
  | Readonly<{ kind: 'dropped'; cseq: number }>

function targetOf(request: VerbRequest): InstanceId {
  return request.verb === 'add' ? request.obj.id : request.id
}

/* --- 본체 ------------------------------------------------------------------------ */

export function createChangerTool(deps: ChangerToolDeps): ChangerTool {
  const { catalog, config, bounds, palette, me } = deps

  const strip = pickChangerCatalog(catalog, deps.seed)
  const firstEntry = strip[0]
  if (firstEntry === undefined) {
    // 놓을 소품이 하나도 없으면 생김이 성립하지 않는다. 빈 스트립을 그리는 것보다 즉시 터지는 편이 싸다(`editor.ts`와 같은 결정).
    throw new Error('createChangerTool: 카탈로그에 놓을 수 있는 소품이 없다')
  }

  /** 호스트가 수용한 것까지의 초안. `pending`이 비어 있으면 화면의 초안과 같다. */
  let confirmed: RoomState = deps.initialDraft
  let draft: RoomState = deps.initialDraft
  const pending: Pending[] = []
  let mode: Mode = { kind: 'idle' }
  /** 마지막으로 든 소품. 생김을 다시 열면 이것부터다. */
  let lastCatalogId: CatalogId = firstEntry.id
  let addSeq = 0
  let disposed = false

  /* --- 조회 --- */

  function desk() {
    return deps.view().desk
  }

  /** 건드린 물건 = 호스트가 확정한 것 + 답을 기다리는 요청의 대상 - 답을 기다리는 되돌리기. `dropped`는 초안에 없으니 세지 않는다. */
  function touchedIds(items: readonly Pending[] = pending): ReadonlySet<InstanceId> {
    const ids = new Set(desk()?.touched ?? [])
    for (const item of items) {
      if (item.kind === 'apply') ids.add(item.targetId)
      else if (item.kind === 'undo') ids.delete(item.id)
    }
    return ids
  }

  /** 답을 기다리는 요청이 개수에 미치는 양. 적용은 +1, 되돌리기는 -1, `dropped`는 0. */
  function pendingDelta(): number {
    return pending.reduce((count, item) => count + (item.kind === 'apply' ? 1 : item.kind === 'undo' ? -1 : 0), 0)
  }

  /** 표시용 사용 개수(머리말). 호스트의 `used`에 답을 기다리는 요청을 얹는다. */
  function usedCount(): number {
    return (desk()?.used ?? 0) + pendingDelta()
  }

  /** 표시용 남은 개수. 호스트가 센 `budgetLeft`(리듀서 `budgetLeftOf`)에서 답을 기다리는 요청만 뺀다 - 예산 규칙은 여기 없다. */
  function budgetLeft(): number {
    return (desk()?.budgetLeft ?? 0) - pendingDelta()
  }

  /** 검증 문맥. 호스트(`matchChange`)와 같은 함수(`changerVerbContext`)에서 같은 스위치를 받는다. */
  function ctx(changedIds: ReadonlySet<InstanceId> = touchedIds()): VerbContext {
    // 손댈 수 있는 사각형은 **내 구획**이다(M11) - 호스트가 뷰로 내린 값 그대로이고, 여기서 좌표를 만들지 않는다.
    // 없으면(구획 없는 판) 검사가 없다: 규칙의 답은 언제나 호스트의 것이고 도구는 그 답을 미리 비출 뿐이다.
    return changerVerbContext(catalog, config, bounds, changedIds, deps.view().zoneRect ?? undefined)
  }

  function selectedId(): InstanceId | null {
    return mode.kind === 'selected' || mode.kind === 'moving' ? mode.id : null
  }

  /**
   * 갈아 끼우기의 문맥 - 고른 물건을 건드린 집합에서 뺀 것(머리말). 지금 초안으로 재면 이미 건드린 물건은 언제나
   * `already-changed`라 다섯이 전부 회색이 되고, 그 화면이 정확히 "왜 아무것도 안 되지"였다.
   * 방은 되돌리지 않는다 - 다음 회전·다음 색은 **지금 보이는 것**에서 골라야 두 번째 누름이 180°가 된다.
   */
  function editCtx(id: InstanceId | null): VerbContext {
    const touched = touchedIds()
    if (id === null || !touched.has(id)) return ctx(touched)
    const without = new Set(touched)
    without.delete(id)
    return ctx(without)
  }

  /** 고른 물건이 이미 자기 변화를 갖고 있는가. 그러면 그 물건의 동사는 갈아 끼우기라 예산을 쓰지 않는다(`changerLocks.lockFor`). */
  function reediting(): boolean {
    const id = selectedId()
    return id !== null && touchedIds().has(id)
  }

  /**
   * 내가 이번 라운드에 놓은 물건인가. 원본에 없으면 그 물건의 변화는 `add` 하나뿐이라 갈아 끼우기도 `add`다.
   * 초안에 있는지까지 보는 이유는 되돌리기가 끼어든 틈에 사라진 id로 `add`를 짓지 않기 위해서다.
   */
  function isMine(id: InstanceId): boolean {
    return !deps.original.objects.has(id) && draft.objects.has(id)
  }

  function entryOf(obj: ObjectInstance | undefined): CatalogEntry | undefined {
    return obj === undefined ? undefined : catalog.get(obj.catalogId)
  }

  /**
   * 그 물건이 무슨 소품인가. **이름이 아니라 id를 돌려준다** - `input/`은 사전을 보지 않는다
   * (`changerToolTypes.ts`의 `zoneId` 주석과 같은 규칙). 없앤 물건은 초안에 없으므로 원본에서 찾는다.
   */
  function catalogIdOf(id: InstanceId): CatalogId | null {
    return (draft.objects.get(id) ?? deps.original.objects.get(id))?.catalogId ?? null
  }

  function undoTarget(): InstanceId | null {
    const touched = [...touchedIds()]
    const selected = selectedId()
    if (selected !== null && touched.includes(selected)) return selected
    return touched.at(-1) ?? null
  }

  /* --- 바 뷰 --- */

  /** 띠가 그릴 모양. 조립은 `changerToolView.buildChangerBarView`(순수)다 - 여기는 도구가 든 것을 한 번 넘긴다. */
  function barView(): ChangerBarView {
    return buildChangerBarView({
      draft,
      mode,
      strip,
      palette,
      catalog,
      me,
      // 잠금은 **갈아 끼울 수 있는가**까지 본 문맥에서 잰다(머리말). 예산 면제는 수를 부풀리는 대신 `reediting`이 말한다 -
      // 화면에 뜨는 남은 개수는 언제나 진짜 수여야 한다.
      ctx: editCtx(selectedId()),
      reediting: reediting(),
      budget: desk()?.budget ?? 0,
      used: usedCount(),
      budgetLeft: budgetLeft(),
      selectedId: selectedId(),
      undoId: undoTarget(),
      catalogIdOf,
      zoneId: deps.view().zoneId,
    })
  }

  /* --- 상태 전이 --- */

  function refresh(): void {
    if (disposed) return
    deps.onView(barView())
  }

  function setMode(next: Mode): void {
    mode = next
    // 놓기가 끝났거나 접혔으면 고스트를 끈다 - 남으면 놓지도 않은 자리에 유령이 선다.
    if (next.kind !== 'adding' && next.kind !== 'moving') deps.onPreview?.(null)
    const id = selectedId()
    const obj = id === null ? undefined : draft.objects.get(id)
    deps.onSelectionChanged?.(obj, entryOf(obj))
    refresh()
  }

  function setDraft(next: RoomState): void {
    if (next === draft) return
    draft = next
    deps.onDraft(draft)
  }

  /** 요청 하나를 초안에 미리 그리고 보낸다. verbs에 걸리면 보내지 않고 이유만 알린다(자리 탭은 미리 잠글 수 없다). */
  function request(req: VerbRequest): boolean {
    const result = applyVerbRequest(draft, req, me, ctx())
    if (!result.ok) {
      deps.onLocalReject(result.reason)
      return false
    }
    const cseq = deps.send({ type: 'applyChange', request: req })
    pending.push({ kind: 'apply', cseq, request: req, targetId: targetOf(req), after: result.state })
    setDraft(result.state)
    return true
  }

  /** 되돌리기의 로컬 판. 호스트(`matchChange.onUndoChange`)와 **같은 함수**(`core/verbs.restoreOriginal`)다. */
  function undoLocally(room: RoomState, id: InstanceId): VerbResult {
    return restoreOriginal(room, deps.original, id, catalog, bounds)
  }

  /**
   * 갈아 끼우기 - 되돌리기 하나 + 새 요청 하나를 **한 손으로** 보낸다(머리말).
   *
   * 순서가 이것이어야 하는 이유는 호스트가 그 순서로 처리하기 때문이다. `undoChange`가 그 물건을 원본으로 되돌리며
   * `touched`에서 빼고, 곧바로 온 `applyChange`가 빈 자리에 새 변화를 넣는다. 초안도 같은 순서로 앞서간다 -
   * `build`가 받는 방은 **되돌린 뒤**의 방이다.
   *
   * 둘 중 하나가 거부되면 `rebuild`가 남은 것을 다시 돌린다. 되돌리기가 거부되면(원래 자리를 그새 남이 막았다)
   * 뒤의 요청은 여전히 `already-changed`라 함께 떨어진다 - 화면은 갈아 끼우기 전으로 돌아가고, 그것이 옳다.
   */
  function reedit(id: InstanceId, build: (undone: RoomState) => VerbRequest): boolean {
    const undone = undoLocally(draft, id)
    if (!undone.ok) {
      deps.onLocalReject(undone.reason)
      return false
    }
    const req = build(undone.state)
    const without = new Set(touchedIds())
    without.delete(id)
    const result = applyVerbRequest(undone.state, req, me, ctx(without))
    if (!result.ok) {
      deps.onLocalReject(result.reason)
      return false
    }
    pending.push({ kind: 'undo', cseq: deps.send({ type: 'undoChange', id }), id, after: undone.state })
    const cseq = deps.send({ type: 'applyChange', request: req })
    pending.push({ kind: 'apply', cseq, request: req, targetId: targetOf(req), after: result.state })
    setDraft(result.state)
    return true
  }

  /** 값 하나를 고치는 세 동사. 자리·회전·색은 같은 길을 지나므로 갈래를 한 곳에 모은다. */
  type FieldEdit =
    | Readonly<{ verb: 'move'; cell: Cell3 }>
    | Readonly<{ verb: 'rotate'; rot: Rot }>
    | Readonly<{ verb: 'recolor'; colorId: PaletteIndex }>

  /** 내가 놓은 물건이면 그 `add`의 필드를 고치고, 아니면 그 동사 그대로다(머리말). */
  function fieldRequest(id: InstanceId, obj: ObjectInstance, edit: FieldEdit): VerbRequest {
    if (isMine(id)) {
      const patch =
        edit.verb === 'move' ? { cell: edit.cell } : edit.verb === 'rotate' ? { rot: edit.rot } : { colorId: edit.colorId }
      return { verb: 'add', obj: { ...obj, ...patch } }
    }
    return edit.verb === 'move'
      ? { verb: 'move', id, cell: edit.cell }
      : edit.verb === 'rotate'
        ? { verb: 'rotate', id, rot: edit.rot }
        : { verb: 'recolor', id, colorId: edit.colorId }
  }

  /** 처음 건드리는 물건이면 새 변화, 이미 건드린 물건이면 갈아 끼우기. 부르는 쪽은 이 갈래를 알 필요가 없다. */
  function editField(id: InstanceId, edit: FieldEdit): boolean {
    const obj = draft.objects.get(id)
    if (obj === undefined) return false
    const req = fieldRequest(id, obj, edit)
    const ok = touchedIds().has(id) ? reedit(id, () => req) : request(req)
    // 고른 채로 남는 동사가 생겼으므로 여기서 직접 그린다 - 전에는 뒤따르던 `setMode({kind:'idle'})`가 대신 그려 주고 있었다.
    if (ok) refresh()
    return ok
  }

  /** 그 물건 하나를 되돌린다. `undo()`는 되돌릴 것을 스스로 고르지만 여기는 부르는 쪽이 고른 것을 되돌린다. */
  function undoOne(id: InstanceId): boolean {
    const result = undoLocally(draft, id)
    if (!result.ok) {
      deps.onLocalReject(result.reason)
      return false
    }
    pending.push({ kind: 'undo', cseq: deps.send({ type: 'undoChange', id }), id, after: result.state })
    setDraft(result.state)
    refresh()
    return true
  }

  /**
   * 확정 초안 위에 남은 요청을 다시 돌린다. 이제 안 되는 것은 초안에서 빼되 자리는 `dropped`로 남긴다(머리말) - 호스트도
   * 같은 답(거부)을 낼 것이고, 그 거부가 이 자리를 소비해야 뒤의 요청이 제자리를 지킨다.
   * 건드린 집합은 **여기까지 살아 있는 요청**으로만 세운다 - 떨어진 요청의 대상을 이미 건드린 것으로 치면 그 뒤 요청이 헛되이 걸린다.
   */
  function rebuild(): void {
    let room = confirmed
    const kept: Pending[] = []
    for (const item of pending) {
      if (item.kind === 'dropped') {
        kept.push(item)
        continue
      }
      const result =
        item.kind === 'apply' ? applyVerbRequest(room, item.request, me, ctx(touchedIds(kept))) : undoLocally(room, item.id)
      if (!result.ok) {
        kept.push({ kind: 'dropped', cseq: item.cseq })
        continue
      }
      room = result.state
      kept.push({ ...item, after: room })
    }
    pending.splice(0, pending.length, ...kept)
    setDraft(room)
    // 고른 물건이 되돌림으로 사라졌을 수 있다(없어짐이 거부된 경우는 반대로 다시 나타난다). 외곽선을 다시 맞춘다.
    const selected = selectedId()
    setMode(selected !== null && !draft.objects.has(selected) ? { kind: 'idle' } : mode)
  }

  function dropPending(at: number): void {
    if (at < 0) return
    pending.splice(at, 1)
    rebuild()
  }

  /* --- 탭 --- */

  function placeAt(clientX: number, clientY: number): Cell3 | null {
    const result = deps.pick(clientX, clientY, draft, 'place')
    return result !== null && result.kind === 'place' ? result.cell : null
  }

  function nextAddId(): InstanceId {
    let id: InstanceId
    do {
      addSeq += 1
      id = `${me}-add-${String(addSeq)}`
    } while (draft.objects.has(id) || deps.original.objects.has(id))
    return id
  }

  /** 자리를 고르는 중인가. 두 모드가 그렇고, 그 밖에는 드래그가 화면을 옮긴다. */
  function isPlacing(): boolean {
    return deps.view().active && (mode.kind === 'adding' || mode.kind === 'moving')
  }

  /**
   * 미리보기 - 손가락이 가리키는 자리를 **초안이 아니라 고스트로** 비춘다(`ChangerPreview` 주석).
   *
   * `valid`는 실제로 그 요청을 verbs에 돌려 본 결과다. 여기서 경계·점유를 비교하면 그것이 규칙의 두 번째
   * 사본이고, 고스트가 초록인데 놓으면 거부되는 순간 미리보기는 도움이 아니라 거짓말이 된다.
   */
  function aimAt(clientX: number, clientY: number): void {
    if (deps.onPreview === undefined) return
    if (!isPlacing()) {
      deps.onPreview(null)
      return
    }
    const spot = placeAt(clientX, clientY)
    if (spot === null) {
      deps.onPreview(null)
      return
    }
    if (mode.kind === 'moving') {
      const moving = draft.objects.get(mode.id)
      if (moving === undefined) {
        deps.onPreview(null)
        return
      }
      // 옮김은 높이를 바꾸지 않는다 - 탭이 만드는 요청과 **같은 셀**을 비춰야 미리보기가 결과와 같다.
      const cell: Cell3 = { x: spot.x, y: moving.cell.y, z: spot.z }
      const probe = applyVerbRequest(draft, fieldRequest(mode.id, moving, { verb: 'move', cell }), me, editCtx(mode.id))
      deps.onPreview({ cell, catalogId: moving.catalogId, rot: moving.rot, valid: probe.ok })
      return
    }
    if (mode.kind !== 'adding') return
    const entry = catalog.get(mode.catalogId)
    if (entry === undefined) {
      deps.onPreview(null)
      return
    }
    const probe = applyVerbRequest(
      draft,
      { verb: 'add', obj: { id: nextAddId(), catalogId: entry.id, cell: spot, rot: ADD_ROT, colorId: entry.defaultColorId } },
      me,
      ctx(),
    )
    deps.onPreview({ cell: spot, catalogId: entry.id, rot: ADD_ROT, valid: probe.ok })
  }

  function tapAt(clientX: number, clientY: number): void {
    if (!deps.view().active) return

    if (mode.kind === 'moving') {
      const spot = placeAt(clientX, clientY)
      if (spot === null) return
      // 자리 탭은 표면 스냅(바닥이면 y=0)인데 옮김은 높이를 바꾸지 않는다(`core/verbs.shouldKeepHeightOnMove` - 생성기의
      // `tryMove`와 같은 규칙). 벽의 거울을 옮기려고 바닥을 누르면 거울은 그 컬럼의 **벽 높이**로 간다 - 요청의 y를 물건의 y로
      // 두어 호스트의 거부 대신 변화 하나를 얻는다. 규칙의 판단은 여전히 verbs다(`request`가 같은 집행자로 초안에 먼저 돌린다).
      // 고른 물건이 초안에서 사라졌으면(되돌림이 끼어든 틈) 자리를 그대로 보내 verbs가 `unknown-instance`로 답하게 둔다 - 여기서
      // 조용히 삼키면 판단이 도구에 한 줄 생긴다.
      const moving = draft.objects.get(mode.id)
      const cell: Cell3 = moving === undefined ? spot : { x: spot.x, y: moving.cell.y, z: spot.z }
      // 옮긴 뒤에도 고른 채로 남는다 - 자리를 잘못 짚었을 때 다시 [옮김]을 눌러 고치는 길이 있어야 한다.
      if (editField(mode.id, { verb: 'move', cell })) setMode({ kind: 'selected', id: mode.id })
      return
    }

    if (mode.kind === 'adding') {
      const cell = placeAt(clientX, clientY)
      const entry = catalog.get(mode.catalogId)
      if (cell === null || entry === undefined) return
      // 생김은 놓은 뒤에도 그 소품을 든 채로 남는다(머리말 "탭 수"). 초안은 `request`가 이미 그렸다.
      request({ verb: 'add', obj: { id: nextAddId(), catalogId: entry.id, cell, rot: ADD_ROT, colorId: entry.defaultColorId } })
      refresh()
      return
    }

    const picked = deps.pick(clientX, clientY, draft, 'search')
    if (picked !== null && picked.kind === 'object') {
      const obj = draft.objects.get(picked.instanceId)
      if (obj !== undefined) {
        // 남의 구획 물건은 **고르는 단계에서** 막는다(M11). 고르게 두면 팔레트가 다섯 개 다 잠긴 채 서고, 그 화면은
        // "왜 아무것도 안 되지"로 읽힌다 - 4.3절이 말하는 선택 불가는 그보다 앞이다. 판단은 verbs의 것 그대로
        // (`isInAllowedRect`)라 사본이 없고, 최종 판정은 여전히 호스트다.
        if (!isInAllowedRect(obj, ctx())) {
          deps.onOutOfZone?.()
          return
        }
        setMode({ kind: 'selected', id: picked.instanceId })
        return
      }
    }
    // 빈 곳 · 허공 · 지운 자리는 "그 물건 말고"다.
    if (mode.kind !== 'idle') setMode({ kind: 'idle' })
  }

  /* --- 팔레트 --- */

  function pickVerb(verb: Verb): void {
    const id = selectedId()
    const inputs = { draft, ctx: editCtx(id), me, paletteSize: palette.length, selectedId: id, budgetLeft: budgetLeft(), reediting: reediting() }
    if (!deps.view().active || lockFor(verb, inputs) !== null) return

    if (verb === 'add') {
      setMode(mode.kind === 'adding' ? { kind: 'idle' } : { kind: 'adding', catalogId: lastCatalogId })
      return
    }

    if (id === null) return
    switch (verb) {
      case 'remove':
        // 내가 놓은 물건을 없애는 것은 **되돌리기**다 - 놓지 않은 것으로 만드는 것이지 그 자리에 새 변화를 두는 것이 아니다.
        if (isMine(id) ? undoOne(id) : touchedIds().has(id) ? reedit(id, () => ({ verb: 'remove', id })) : request({ verb: 'remove', id })) {
          setMode({ kind: 'idle' })
        }
        return
      case 'move':
        // 다시 누르면 접는다 - 옮길 자리를 고르다 마음이 바뀌는 길이 있어야 한다.
        setMode(mode.kind === 'moving' ? { kind: 'selected', id } : { kind: 'moving', id })
        return
      case 'rotate': {
        // 다음 회전은 **지금 보이는 것**에서 고른다(머리말) - 되돌린 원본에서 고르면 몇 번을 눌러도 90°가 나온다.
        const next = nextVisibleRot(draft, id, editCtx(id))
        // 고른 채로 남는다. 여기서 풀면 한 칸 돌리고 끝이라 90°가 이 게임의 유일한 회전이 된다.
        if ('rot' in next) editField(id, { verb: 'rotate', rot: next.rot })
        return
      }
      case 'recolor': {
        const next = nextVisibleColor(draft, id, editCtx(id), palette.length)
        if ('colorId' in next) editField(id, { verb: 'recolor', colorId: next.colorId })
        return
      }
    }
  }

  function pickCatalog(catalogId: CatalogId): void {
    if (!deps.view().active || !strip.some((entry) => entry.id === catalogId)) return
    if (budgetLeft() <= 0 || addLock(draft, me, ctx()) !== null) return
    lastCatalogId = catalogId
    setMode({ kind: 'adding', catalogId })
  }

  function pickColor(colorId: PaletteIndex): void {
    const id = selectedId()
    if (!deps.view().active || id === null || (budgetLeft() <= 0 && !reediting())) return
    // 회전과 같다 - 고른 채로 남아야 팔레트에서 색을 몇 번이고 고쳐 볼 수 있다.
    editField(id, { verb: 'recolor', colorId })
  }

  function undo(): void {
    if (!deps.view().active) return
    const id = undoTarget()
    if (id === null) return
    const result = undoLocally(draft, id)
    if (!result.ok) {
      deps.onLocalReject(result.reason)
      return
    }
    const cseq = deps.send({ type: 'undoChange', id })
    pending.push({ kind: 'undo', cseq, id, after: result.state })
    setDraft(result.state)
    setMode({ kind: 'idle' })
  }

  /**
   * [다 했다]. 걸기(M7)는 띠의 스테퍼 값 그대로다 - 0(또는 값 없음)이면 걸지 않은 것이라 `null`. 1 이상은 그대로 싼다.
   * 상한·정수 여부를 여기서 보지 않는 이유는 그 규칙이 호스트(`raise.normalizeWager`)의 것이라서다 - 잘못된 값은 걸기만 없는
   * 커밋으로 접히고 60초의 작업은 살아남는다.
   */
  function commit(wager = 0): void {
    if (!deps.view().active) return
    const wagered: ChangerToolWager | null = wager > 0 ? { unfoundAtLeast: wager } : null
    deps.send({ type: 'commitChanges', wager: wagered })
    setMode({ kind: 'idle' })
  }

  /* --- 호스트의 답 --- */

  /**
   * 수용 하나. 머리의 요청이 확정 초안이 된다. 머리가 `dropped`면 호스트가 우리가 떨어뜨린 요청을 수용한 것이라 초안을
   * 알 길이 없다(호스트는 방을 다시 보내지 않는다) - 확정 초안은 그대로 두고 자리만 비운다. 같은 verbs를 같은 순서로 돌리는
   * 한 이 길은 열리지 않는다.
   */
  function onProgress(): void {
    const head = pending.shift()
    if (head !== undefined && head.kind !== 'dropped') confirmed = head.after
    refresh()
  }

  refresh()

  return {
    tapAt,
    aimAt,
    isPlacing,
    pickVerb,
    pickCatalog,
    pickColor,
    undo,
    commit,
    onProgress,
    onRejected: () => dropPending(0),
    onRequestDropped: (cseq) => dropPending(pending.findIndex((item) => item.cseq === cseq)),
    onView: refresh,
    getDraft: () => draft,
    getBarView: barView,
    dispose(): void {
      disposed = true
      deps.onSelectionChanged?.(undefined, undefined)
    },
  }
}
